import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type {
  User,
  InsurerContext,
  BrokerContext,
  ChannelContext,
  CustomerContext,
  CountryContext,
  LoginType,
} from '@/types';
import { forgetAllRememberedFilters } from '@/utils/rememberedFilters';

// Tenant context can be any of the role-specific contexts
type TenantContext = InsurerContext | BrokerContext | ChannelContext | CustomerContext | null;

export const AUTH_STORAGE_KEY = 'policy-auth-storage';

/**
 * 9c: strip tokens a pre-9c bundle left behind in localStorage.
 *
 * Until this release the store persisted BOTH the access token and the 7-day
 * refresh token under `policy-auth-storage`, so anything that could run a
 * script in this page could read a long-lived credential straight out of
 * storage. The refresh token now lives in an httpOnly cookie the page cannot
 * see and the access token is held in memory only.
 *
 * Dropping them from `partialize` stops new writes, but a browser that ran the
 * old bundle still has the old value sitting in storage until the next write,
 * so it is removed here, once, before the store rehydrates from it.
 */
export function purgeLegacyPersistedTokens(): void {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as { state?: Record<string, unknown> };
    const state = parsed?.state;
    if (!state || typeof state !== 'object') return;
    if (!('accessToken' in state) && !('refreshToken' in state)) return;
    delete state.accessToken;
    delete state.refreshToken;
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(parsed));
  } catch {
    // Corrupt JSON, or storage blocked (private window, blocked site data):
    // there is nothing to purge and nothing that should stop the app loading.
  }
}

purgeLegacyPersistedTokens();

interface AuthState {
  user: User | null;
  /**
   * In memory only, never persisted (9c). A reload therefore starts with no
   * access token; `restoreSession` in services/api.ts mints a new one from the
   * refresh cookie before the first protected route renders.
   */
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  loginType: LoginType | null;
  tenantContext: TenantContext;
  countryContext: CountryContext | null;

  // Actions
  setUser: (user: User | null) => void;
  setAccessToken: (access: string) => void;
  login: (user: User, access: string, loginType?: LoginType, context?: TenantContext, countryContext?: CountryContext | null) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  setTenantContext: (context: TenantContext) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      isLoading: true,
      loginType: null,
      tenantContext: null,
      countryContext: null,

      setUser: (user) => set({ user }),

      setAccessToken: (access) =>
        set({
          accessToken: access,
          isAuthenticated: true,
        }),

      login: (user, access, loginType, context, countryContext) =>
        set({
          user,
          accessToken: access,
          isAuthenticated: true,
          isLoading: false,
          loginType,
          tenantContext: context,
          countryContext: countryContext ?? null,
        }),

      logout: () => {
        // Q-01: session-remembered list filters belong to the person who set
        // them; the next login on this browser starts clean.
        forgetAllRememberedFilters();
        set({
          user: null,
          accessToken: null,
          isAuthenticated: false,
          isLoading: false,
          loginType: null,
          tenantContext: null,
          countryContext: null,
        });
      },

      setLoading: (loading) => set({ isLoading: loading }),

      setTenantContext: (context) => set({ tenantContext: context }),
    }),
    {
      name: AUTH_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      // No token is listed here on purpose (9c). `isAuthenticated` is kept so a
      // reload knows there was a session worth restoring; it is a hint, not a
      // credential -- every request is still authorised by the server.
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        loginType: state.loginType,
        tenantContext: state.tenantContext,
        countryContext: state.countryContext,
      }),
    }
  )
);
